import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import SeoHelmet from '../../common/SeoHelmet';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Fade from '@mui/material/Fade';
import ProjectRowSkeleton from './components/ProjectRowSkeleton';
import ProjectsSearch from './components/ProjectsSearch';
import ProjectsSort from './components/ProjectsSort';
import ProjectRow from './components/ProjectRow';
import CreateProjectDialog from './components/CreateProjectDialog';
import ProjectsSearchEmpty from './components/ProjectSearchEmpty';

const ProjectsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [highlightedProjectId, setHighlightedProjectId] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('lastModified'); // DEFAULT

  useEffect(() => {
    let isMounted = true;

    const fetchProjects = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get('/api/v1/project');
        if (!isMounted) return;
        setProjects(res.data.data.projects);
      } catch (err) {
        if (!isMounted) return;
        setError(err?.response?.data?.message || 'Failed to load projects.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProjects();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCreateProject = async (name) => {
    try {
      const res = await axios.post('/api/v1/project', { name });

      // accept either { project } or direct doc, depending on your controller
      const created = res?.data?.data?.project || res?.data?.data?.doc || res?.data?.data;

      if (!created?.id) {
        throw new Error('Unexpected server response.');
      }

      // Add to top, then sort by updatedAt desc (matches your index intention)
      setProjects((prev) => {
        const next = [created, ...prev];
        next.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        return next;
      });
      setHighlightedProjectId(created.id);
      // clear highlight after 2 seconds
      setTimeout(() => {
        setHighlightedProjectId(null);
      }, 3000);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create project.';

      // Friendly duplicate-name message (Mongo unique index often returns 11000)
      const code = err?.response?.data?.code;
      const raw = String(msg).toLowerCase();

      if (code === 11000 || raw.includes('duplicate') || raw.includes('e11000')) {
        throw new Error('A project with this name already exists.');
      }

      throw new Error(msg);
    }
  };

  const handleDeleted = (deletedId) => {
    setProjects((prev) => prev.filter((p) => p.id !== deletedId));
  };

  const handleStatusChanged = (updated) => {
    setProjects((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));

    setHighlightedProjectId(updated.id);

    setTimeout(() => {
      setHighlightedProjectId(null);
    }, 2000);
  };

  const STATUS_ORDER = {
    in_progress: 0,
    finished: 1,
    archived: 2,
  };

  const visibleProjects = useMemo(() => {
    // 1) filter
    const q = search.trim().toLowerCase();
    let list = !q ? projects : projects.filter((p) => (p?.name || '').toLowerCase().includes(q));

    // 2) sort (copy before sort!)
    const sorted = [...list];

    switch (sortBy) {
      case 'projectName':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;

      case 'createdAt':
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;

      case 'lastModified':
        sorted.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        break;

      case 'status':
        sorted.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
        break;

      case 'numberOfCalculations':
        sorted.sort((a, b) => (b.calculationsCount || 0) - (a.calculationsCount || 0));
        break;

      default:
        break;
    }

    return sorted;
  }, [projects, search, sortBy]);

  return (
    <>
      <SeoHelmet title="GeoCalc | Projects Dashboard" indexable={false} />
      <Header />
      <div className="projects-dashboard-container">
        <h1>Projects Dashboard</h1>
        <div className="projects-dashboard-header">
          <div className="projects-dashboard-header-left">
            <ProjectsSearch value={search} onChange={setSearch} />
            <ProjectsSort value={sortBy} onChange={setSortBy} />
          </div>
          <div className="projects-dashboard-header-right">
            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              className="projectsCreateButton"
            >
              New Project
            </Button>
          </div>
          <CreateProjectDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreateProject} />
        </div>
        {!loading && error ? (
          <>
            <div className="projects-error-box">
              <div className="projects-error-title">Unable to load projects</div>
              <div className="projects-error-message">{error}</div>
            </div>
          </>
        ) : !loading && search && projects.length > 0 && visibleProjects.length === 0 ? (
          <ProjectsSearchEmpty query={search} onClear={setSearch} />
        ) : !loading && projects.length === 0 ? (
          <>
            <div className="projects-empty-state">
              <div className="projects-empty-state-title">No projects yet</div>
              <div className="projects-empty-state-text">Create a project to organize and manage your saved calculations.</div>
            </div>
          </>
        ) : (
          <>
            <div className="projects-dashboard-list-header">
              <div className="projects-dashboard-list-header-item">Project Name</div>
              <div className="projects-dashboard-list-header-item">Created At</div>
              <div className="projects-dashboard-list-header-item">Last Modified</div>
              <div className="projects-dashboard-list-header-item">Status</div>
              <div className="projects-dashboard-list-header-item"># Calculations</div>
              <div className="projects-dashboard-list-header-item">Actions</div>
            </div>

            <div className="projects-dashboard-list">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <ProjectRowSkeleton key={i} />)
              ) : (
                <Fade in timeout={200}>
                  <div className="projects-dashboard-list-fade">
                    {visibleProjects.map((p) => (
                      <ProjectRow
                        key={p.id}
                        project={p}
                        highlight={p.id === highlightedProjectId}
                        onDeleted={handleDeleted}
                        onStatusChanged={handleStatusChanged}
                      />
                    ))}
                  </div>
                </Fade>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ProjectsDashboard;
